import { useEffect, useState } from "react";
import {
  ArrowClockwise, CheckCircle, Download, Lightbulb, Prohibit, SpinnerGap, X, XCircle,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function jobTitle(job) {
  switch (job.job_type) {
    case "generate_all":
    case "regenerate_from_design":
      return "Generating All Drawings";
    case "generate_3d_model":
      return "Generating 3D Model";
    case "print_drawing":
      return "Generating MFG Print Drawing";
    default:
      return `Generating ${job.drawing_type || "drawing"}`;
  }
}

const ACCENT = {
  pending: "border-l-[var(--c-muted)]",
  running: "border-l-[var(--c-accent)]",
  done: "border-l-[var(--c-green)]",
  failed: "border-l-[var(--c-red)]",
  cancelled: "border-l-[var(--c-muted)]",
};

export default function DrawingJobProgress({ job, onCancel, onRetry, onDismiss, onPrioritise }) {
  const active = job.status === "pending" || job.status === "running";
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);

  const elapsed = job.elapsed_seconds != null
    ? Math.round(job.elapsed_seconds)
    : job.tracked_at ? Math.round((Date.now() - job.tracked_at) / 1000) : 0;
  const pct = Math.round(job.progress?.percent || 0);
  const statuses = job.progress?.drawing_statuses || [];

  return (
    <div data-testid={`drawing-job-card${job.job_id ? `-${job.job_id}` : ""}`} data-status={job.status} data-job-id={job.job_id || ""}
      className={`bg-[var(--c-surface)]/95 backdrop-blur border border-[var(--c-border)] border-l-[3px] ${ACCENT[job.status] || ""} rounded-sm px-3 py-2.5 shadow-xl`}>
      <div className="flex items-center gap-2">
        {job.status === "done" && <CheckCircle size={14} weight="fill" className="text-[var(--c-green)] shrink-0" />}
        {job.status === "failed" && <XCircle size={14} weight="fill" className="text-[var(--c-red)] shrink-0" />}
        {job.status === "cancelled" && <Prohibit size={14} className="text-[var(--c-muted)] shrink-0" />}
        {active && <SpinnerGap size={14} className="text-[var(--c-accent)] animate-spin shrink-0" />}
        <span className="flex-1 text-[12px] font-medium text-[var(--c-text)] truncate" data-testid="job-title">
          {job.status === "done" ? (job.result?.summary || "Drawing generated")
            : job.status === "failed" ? "Drawing generation failed"
            : job.status === "cancelled" ? "Generation cancelled"
            : jobTitle(job)}
        </span>
        {(active || job.status === "done") && (
          <span className="text-[10px] font-mono text-[var(--c-muted)] shrink-0" data-testid="job-elapsed">
            {job.status === "done" && job.duration_seconds != null ? `${Math.round(job.duration_seconds)}s` : `${elapsed}s`}
          </span>
        )}
      </div>

      {active && (
        <>
          <div className="h-1.5 bg-[var(--c-border)]/60 rounded-full overflow-hidden mt-2">
            <div data-testid="job-progress-bar"
              className="h-full bg-[var(--c-accent)] rounded-full transition-[width] duration-500"
              style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <span className="text-[11px] text-[var(--c-muted)] truncate" data-testid="job-current-step">
              {job.progress?.current_step || "Processing…"}
            </span>
            <span className="text-[10px] font-mono text-[var(--c-accent)] shrink-0">{pct}%</span>
          </div>
          {statuses.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5" data-testid="job-drawing-statuses">
              {statuses.map((s) => (
                <span key={s.type}
                  className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)]/60">
                  <span className={`w-1.5 h-1.5 rounded-full ${s.status === "done" ? "bg-[var(--c-green)]" : s.status === "running" ? "bg-[var(--c-accent)] animate-pulse" : s.status === "failed" ? "bg-[var(--c-red)]" : "bg-[var(--c-muted)]"}`} />
                  <span className="text-[var(--c-muted)]">{s.type}</span>
                </span>
              ))}
            </div>
          )}
          {job.status === "pending" && job.queue && (
            <div className="mt-1.5 px-2 py-1.5 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)]/60"
              data-testid="job-queue-info">
              <div className="text-[10px] text-[var(--c-muted)]">
                Position {job.queue.position} in the workspace queue · {job.queue.limit} jobs run at a time
              </div>
              {(job.queue.ahead || []).slice(0, 4).map((a) => (
                <div key={a.job_id} className="flex items-center gap-1.5 mt-1 text-[10px]"
                  data-testid={`job-queue-ahead-${a.job_id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${a.status === "running" ? "bg-[var(--c-accent)] animate-pulse" : "bg-[var(--c-muted)]"}`} />
                  <span className="text-[var(--c-text)] truncate">
                    {a.job_type === "generate_single" ? (a.drawing_type || "drawing") : (a.job_type || "").replace(/_/g, " ")}
                  </span>
                  <span className="ml-auto font-mono text-[var(--c-muted)] shrink-0">
                    {a.status === "running" ? `${Math.round(a.percent || 0)}%` : "queued"}
                    {a.same_project ? "" : " · other project"}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-1.5 mt-1.5">
            {job.status === "pending" && onPrioritise && (
              <button data-testid="job-prioritise-btn" onClick={() => onPrioritise(job.job_id)}
                className="h-6 px-2.5 rounded-sm bg-[var(--c-amber)]/15 border border-[var(--c-amber)]/40 text-[11px] text-[var(--c-amber)]">
                Stop others & run this first
              </button>
            )}
            <button data-testid="job-cancel-btn" onClick={() => onCancel(job.job_id)}
              className="h-6 px-2.5 rounded-sm border border-[var(--c-border)] text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)]">
              Cancel
            </button>
          </div>
        </>
      )}

      {job.status === "done" && (
        <>
          {Array.isArray(job.result?.files) && job.result.files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="job-downloads">
              {job.result.files.filter((f) => f.download_url).slice(0, 6).map((f, i) => (
                <a key={`${f.format}-${f.drawing_type || ""}-${i}`} data-testid={`job-download-${f.format}`}
                  href={`${BASE}${f.download_url}`} target="_blank" rel="noreferrer"
                  className="h-6 px-2 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)] text-[10px] uppercase font-mono text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
                  <Download size={11} /> {f.format}
                  {f.drawing_type && <span className="normal-case opacity-70">{f.drawing_type}</span>}
                  {f.size_bytes != null && <span className="opacity-60">{Math.round(f.size_bytes / 1024)}K</span>}
                </a>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-1.5">
            <button data-testid="job-dismiss-btn" onClick={() => onDismiss(job.job_id)}
              className="h-6 px-2 rounded-sm text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
              <X size={11} /> Dismiss
            </button>
          </div>
        </>
      )}

      {job.status === "failed" && (
        <>
          <div className="text-[11px] text-[var(--c-red)] mt-1.5 break-words" data-testid="job-error-message">
            {job.error?.message || "Unknown error"}
          </div>
          {job.error?.suggestion && (
            <div className="flex items-start gap-1.5 text-[11px] text-[var(--c-amber)] mt-1.5 px-2 py-1.5 rounded-sm bg-[var(--c-amber)]/10" data-testid="job-error-suggestion">
              <Lightbulb size={12} className="shrink-0 mt-px" /> {job.error.suggestion}
            </div>
          )}
          <div className="flex justify-end gap-1.5 mt-2">
            {job.error?.recoverable !== false && (
              <button data-testid="job-retry-btn" onClick={() => onRetry(job.job_id)}
                className="h-6 px-2.5 rounded-sm bg-[var(--c-accent)]/15 border border-[var(--c-accent)]/40 text-[11px] text-[var(--c-accent)] flex items-center gap-1">
                <ArrowClockwise size={11} /> Retry
              </button>
            )}
            <button data-testid="job-dismiss-btn" onClick={() => onDismiss(job.job_id)}
              className="h-6 px-2 rounded-sm text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
              <X size={11} /> Dismiss
            </button>
          </div>
        </>
      )}

      {job.status === "cancelled" && (
        <div className="flex justify-end gap-1.5 mt-1.5">
          <button data-testid="job-retry-btn" onClick={() => onRetry(job.job_id)}
            className="h-6 px-2.5 rounded-sm bg-[var(--c-accent)]/15 border border-[var(--c-accent)]/40 text-[11px] text-[var(--c-accent)] flex items-center gap-1">
            <ArrowClockwise size={11} /> Restart
          </button>
          <button data-testid="job-dismiss-btn" onClick={() => onDismiss(job.job_id)}
            className="h-6 px-2 rounded-sm text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
            <X size={11} /> Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
